import { useCallback, useEffect, useState } from 'react';

import { Box, Button } from '@mui/material';
import { CloseRounded, NotificationsNoneRounded } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import {
  Container,
  Header,
  Page,
  SectionCards,
  Typography,
  Card,
  Date,
  Title,
  Description,
} from './styles';
import useAuthStore from '../../store/auth';
import {
  getNotificationsByUserAndCompanyIdRequest,
  removeNotification,
  updateNotificationToRead,
} from '../../services/requests/notifications/utils';
import useCompanyStore from '../../store/companies';
import { Notification } from '../../services/requests/notifications';
import useNotificationStore from '../../store/notifications';

export function Notificacoes() {
  const setMenuSelected = useAuthStore(state => state.setMenuSelected);
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const selectedCompany = useCompanyStore(state => state.selectedCompany);
  const notifications = useNotificationStore(state => state.notifications);
  const setNotifications = useNotificationStore(
    state => state.setNotifications,
  );
  const countNotification = useNotificationStore(
    state => state.countNotification,
  );
  const setCountNotification = useNotificationStore(
    state => state.setCountNotification,
  );

  const [listagemNotificacoes, setListagemNotificacoes] = useState<
    Notification[]
  >([]);
  const [listagemNotificacoesAtivas, setListagemNotificacoesAtivas] = useState<
    Notification[]
  >([]);
  const [countNotificationsActivesUnread, setCountNotificationsActivesUnread] =
    useState<number>();

  const handleGoToProfile = useCallback(() => {
    setMenuSelected('profile');
    navigate('/home/dados-pessoais');
  }, [navigate, setMenuSelected]);

  const loadNotifications = useCallback(async () => {
    try {
      const response = await getNotificationsByUserAndCompanyIdRequest(
        user?.id as string,
        selectedCompany?.id as string,
        '1000',
        '1',
      );

      if (response?.status === 200) {
        setListagemNotificacoes(response?.data);
      }
    } catch (err) {
      console.log(err);
    }
  }, [selectedCompany?.id, user?.id]);

  useEffect(() => {
    if (user?.id) {
      loadNotifications();
    }
  }, [user?.id]);

  useEffect(() => {
    if (listagemNotificacoes) {
      const listActives = listagemNotificacoes.filter(
        n => n.canceled_at === null,
      );

      setListagemNotificacoesAtivas(listActives);
      setNotifications(listActives);
    }
  }, [loadNotifications, listagemNotificacoes]);

  useEffect(() => {
    if (listagemNotificacoesAtivas) {
      const listActives = listagemNotificacoes.filter(
        n => n.canceled_at === null,
      );
      const countActivesUnread = listActives.filter(n => !n.read).length;
      setCountNotificationsActivesUnread(countActivesUnread);
      setCountNotification(countActivesUnread);
    }
  }, [loadNotifications, listagemNotificacoesAtivas]);

  const handleChangeToRead = useCallback(
    async (notification: Notification) => {
      try {
        if (!notification.read) {
          const response = await updateNotificationToRead(
            notification?.id,
            user?.id as string,
            selectedCompany?.id as string,
          );

          if (response?.status === 200) {
            loadNotifications();
            navigate(response.data.path);
          }
        }
      } catch (err) {
        console.log(err);
      }
    },
    [loadNotifications, navigate, selectedCompany?.id, user?.id],
  );

  const handleRemoveNotification = useCallback(
    async (id: string) => {
      try {
        const response = await removeNotification(
          id,
          user?.id as string,
          selectedCompany?.id as string,
        );

        if (response?.status === 200) {
          loadNotifications();
        }
      } catch (err) {
        console.log(err);
      }
    },
    [loadNotifications, selectedCompany?.id, user?.id],
  );

  const handleReadAll = useCallback(async () => {
    try {
      const listWithUnRead = listagemNotificacoesAtivas.filter(n => !n.read);

      const response = Promise.all(
        listWithUnRead.map(async n => {
          await updateNotificationToRead(
            n.id,
            user?.id as string,
            selectedCompany?.id as string,
          );
        }),
      );

      if (await response) {
        loadNotifications();
      }
    } catch (err) {
      console.log(err);
    }
  }, [
    listagemNotificacoesAtivas,
    loadNotifications,
    selectedCompany?.id,
    user?.id,
  ]);

  const handleRemoveAll = useCallback(async () => {
    try {
      const listWithRead = listagemNotificacoesAtivas.filter(n => n.read);

      const response = Promise.all(
        listWithRead.map(async n => {
          await removeNotification(
            n.id,
            user?.id as string,
            selectedCompany?.id as string,
          );
        }),
      );

      if (await response) {
        loadNotifications();
      }
    } catch (err) {
      console.log(err);
    }
  }, [
    listagemNotificacoesAtivas,
    loadNotifications,
    selectedCompany?.id,
    user?.id,
  ]);

  return (
    <Container>
      <Page>
        <Header>
          <Typography>Notificações</Typography>
          <Box display="flex" flexDirection="row" gap="20px">
            {countNotification && countNotification > 0 ? (
              <Button
                onClick={handleReadAll}
                sx={{
                  fontWeight: '400',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#206da7',
                  fontFamily: 'Roboto',
                  fontStyle: 'normal',
                  letterSpacing: '0.15px',
                  textTransform: 'initial',
                }}
              >
                Marcar como lidas {`(${countNotification})`}
              </Button>
            ) : null}
            {!countNotification && notifications && notifications.length > 0 ? (
              <Button
                onClick={handleRemoveAll}
                sx={{
                  fontWeight: '400',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#d62237',
                  fontFamily: 'Roboto',
                  fontStyle: 'normal',
                  letterSpacing: '0.15px',
                  textTransform: 'initial',
                }}
              >
                Remover notificações {`(${notifications.length})`}
              </Button>
            ) : null}
            <Button
              onClick={handleGoToProfile}
              variant="outlined"
              sx={{
                display: 'flex',
                gap: '8px',
                padding: '6px 16px 6px 12px',
                fontWeight: '600',
              }}
            >
              <NotificationsNoneRounded /> Configurar
            </Button>
          </Box>
        </Header>
        <SectionCards>
          {notifications && notifications?.length > 0 ? (
            notifications.map((n, idx) =>
              n.canceled_at ? null : (
                <Card
                  sx={{
                    borderTop: idx === 0 ? 'none' : '1px solid #d9d9d9',
                    borderLeft: n.read ? 'none' : '3px solid #206da7',
                    borderTopLeftRadius: idx === 0 ? '6px' : '0px',
                    borderBottomLeftRadius:
                      n === notifications[notifications.length - 1]
                        ? '6px'
                        : '0px',
                    cursor: n.read ? 'default' : 'pointer',
                    justifyContent: n.read ? 'space-between' : 'flex-start',
                  }}
                  onClick={() => handleChangeToRead(n)}
                >
                  <Box width="100%">
                    <Date sx={{ color: n.read ? '#9E9E9E' : '#A1B2DE' }}>
                      {dayjs(n.created_at).format('DD/MM/YYYY - HH:mm')}
                    </Date>
                    <Title sx={{ color: n.read ? '#616161' : '#206da7' }}>
                      {n.title}
                    </Title>
                    <Description sx={{ color: n.read ? '#616161' : '#206da7' }}>
                      {n.description}
                    </Description>
                  </Box>
                  {n.read ? (
                    <Button onClick={() => handleRemoveNotification(n.id)}>
                      <CloseRounded />
                    </Button>
                  ) : null}
                </Card>
              ),
            )
          ) : (
            <Box padding="20px">
              <p>Nenhuma notificação encontrada...</p>
            </Box>
          )}
        </SectionCards>
      </Page>
    </Container>
  );
}
